/**
 * Order Management — shared MUI `sx` tokens for tables, cards, and text clamping.
 */

import { COLORS } from './orderManagementConfig';

export const TH_SX = {
  fontFamily: 'Inter Bold, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: COLORS.muted,
  bgcolor: COLORS.headerBg,
  borderTop: 'none',
  borderLeft: 'none',
  borderRight: 'none',
  borderBottom: `2px solid ${COLORS.border}`,
  py: 1.5,
  px: 2,
  whiteSpace: 'nowrap',
};

export const TD_SX = {
  borderTop: 'none',
  borderLeft: 'none',
  borderRight: 'none',
  borderBottom: `1px solid ${COLORS.rowDivider}`,
  py: 1.5,
  px: 2,
  verticalAlign: 'middle',
  fontFamily: 'Inter Regular, sans-serif',
  fontSize: 13,
  color: COLORS.ink,
};

/** Max 2 lines with ellipsis (used by ClampedText and item promos). */
export const clamp2LineSx = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  minWidth: 0,
};

export const orderListItemCellSx = {
  ...TD_SX,
  py: 1.25,
  border: 'none',
  minWidth: 0,
  maxWidth: 0,
  overflow: 'hidden',
  verticalAlign: 'top',
};

export const orderListQtyCellSx = {
  ...TD_SX,
  py: 1.25,
  border: 'none',
  width: 40,
  minWidth: 40,
  maxWidth: 40,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  verticalAlign: 'top',
  fontFamily: 'Inter Regular, sans-serif',
  fontSize: 13,
  color: COLORS.ink,
};

export const orderListMoneyCellSx = {
  ...TD_SX,
  py: 1.25,
  border: 'none',
  minWidth: 0,
  maxWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textAlign: 'right',
  verticalAlign: 'top',
  fontSize: 13,
  color: COLORS.ink,
};

export const orderListTotalCellSx = {
  ...orderListMoneyCellSx,
  fontFamily: 'Inter Bold, sans-serif',
  fontWeight: 600,
};

/** Detail accordion card shell. */
export const cardSx = {
  bgcolor: COLORS.white,
  borderRadius: '14px',
  border: `1.5px solid ${COLORS.border}`,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  overflow: 'hidden',
};

export const cardHeaderSx = {
  px: 2.5,
  py: 1.5,
  borderBottom: `1px solid ${COLORS.border}`,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
};

export const cardHeaderTitleSx = {
  fontFamily: 'Inter Bold, sans-serif',
  fontSize: 14,
  fontWeight: 700,
  color: '#111827',
  lineHeight: 1.35,
};

/** Highlighted accordion card (e.g. driver carry items). */
export const highlightedCardSx = {
  border: '2px solid #111827',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
};

export const infoCardBodySx = { px: 2.5, py: 2, minWidth: 0 };

export const nestedScrollSx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'auto',
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-thumb': { bgcolor: '#D1D5DB', borderRadius: 3 },
  '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
};

/** Bottom accordion row — compact height; inner areas scroll. */
export const ROW2_CARD_MIN_HEIGHT = 350;

/** Bottom row grid: [Status Update] [Dedicated Drivers] [Map] */
export const BOTTOM_ROW_GRID_COLUMNS = {
  xs: '1fr',
  md: 'minmax(200px, 0.72fr) minmax(0, 2.1fr) minmax(220px, 0.95fr)',
};

/** Top row: scroll inside Order List items (card height stays compact). */
export const TOP_ROW_ORDER_LIST_ITEMS_MAX_HEIGHT = 380;

/** Top row: scroll inside Order Status timeline (avoids stretching sibling cards). */
export const TOP_ROW_ORDER_STATUS_MAX_HEIGHT = 190;
